import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { stringToCamelCase } from '@/services/helpers';
import type { KeysToCamelCase, ToCamelCase } from '@/types/app';

import useDebouncedValue from './useDebouncedValue';

export type TParamFormatters<T extends string> = {
  [K in T as ToCamelCase<K>]?: (value: string) => unknown;
};

export type TFormattedParams<
  T extends string,
  Formatters extends TParamFormatters<T>
> = {
  [K in T]: ToCamelCase<K> extends keyof Formatters
    ? Formatters[ToCamelCase<K>] extends (value: string) => infer R
      ? R
      : string
    : string;
};

export type TSearchParamValuesOptions<
  T extends string,
  Formatters extends TParamFormatters<T> = TParamFormatters<T>
> = {
  delay?: number;
  formatters?: Formatters;
};

const useSearchParamValues = <
  T extends string,
  Formatters extends TParamFormatters<T> = TParamFormatters<T>
>(
  params: readonly T[],
  options?: TSearchParamValuesOptions<T, Formatters>
): KeysToCamelCase<TFormattedParams<T, Formatters>> => {
  const [searchParams] = useSearchParams();
  const { delay, formatters } = options || {};

  const values = useMemo(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    return params.reduce(
      (acc, key) => {
        const rawValue = (urlSearchParams.get(key) ?? '').trim();
        const camelKey = stringToCamelCase(key);
        const formatter = (
          formatters as
            Record<string, ((value: string) => unknown) | undefined> | undefined
        )?.[camelKey];
        (acc as Record<string, unknown>)[camelKey] = formatter
          ? formatter(rawValue)
          : rawValue;
        return acc;
      },
      {} as KeysToCamelCase<TFormattedParams<T, Formatters>>
    );
  }, [searchParams, params, location.search, formatters]);

  const changedValue = useMemo(() => {
    return values;
    // eslint-disable-next-line react-hooks/use-memo
  }, [JSON.stringify(values)]);

  return useDebouncedValue(changedValue, delay);
};

export default useSearchParamValues;
